import { z, type infer as zInfer } from "zod";
import { market } from "@/hooks/firestore/models/market";
import { timestamp } from "@/hooks/firestore/models/timestamp";
import {
  promotionCampaign,
  type PromotionCampaign,
} from "@/hooks/firestore/models/promotionCampaign";

// only TEXT handled for now. Backend may send other formats which we ignore for now. We might do MARKDOWN or something else in future
export const COMMENT_FORMAT = {
  TEXT: "TEXT",
} as const;

const marketCountBucket = z.object({
  SameEventMulti: z.number(),
  SingleOutcome: z.number(),
  total: z.number(),
});

export const bettingEvent = z.object({
  activeMarketCount: z.number(),
  activeMarketCounts: z.object({
    SameEventMulti: z.number(),
    SingleOutcome: z.number(),
  }),
  // more accurate than activeMarketCount(s). Buckets can be missing when empty.
  marketCounts: z
    .object({
      Live: marketCountBucket,
      Prematch: marketCountBucket,
      total: z.number(),
    })
    .partial()
    .optional(),
  attributes: z.record(z.string(), z.any()).optional(),
  competitors: z
    .record(
      z.string(),
      z.object({
        abbreviation: z.string().nullish(),
        name: z.string(),
        type: z.enum(["PLAYER", "FRANCHISE", "RACER", "FLAG"]),
        nationality: z.string().nullish(),
        iconUri: z.string().nullish(),
        number: z.number().optional(),
        silksUrl: z.string().optional(),
        scratched: z.boolean().optional(),
      }),
    )
    .optional(),
  eventId: z.string(),
  eventName: z.string(),
  eventType: z.enum(["MATCH", "SEASON", "TOURNAMENT"]),
  filters: z.array(z.string()),
  hub: z.enum(["sports", "esports"]),
  mainMarket: market.extend({ id: z.string() }).optional(),
  nextBetStop: timestamp.optional(),
  parentEventFilter: z.string(),
  parentEventName: z.string(),
  campaignIds: z.array(z.string()).optional(),
  comments: z
    .array(
      z.object({
        comment: z.string(),
        commentFormat: z.string().optional(), // just string for now, so markdown/whatever doesn't make it fall apart
        locales: z.array(z.string()).optional(),
      }),
    )
    .optional(),
  hasPromotionalMarkets: z.boolean().optional(),
  promotionIsAvailable: z.boolean().optional(),
  promotionCampaigns: z.array(promotionCampaign).nullish(),
  relatedEventIds: z
    .record(z.string(), z.enum(["MATCH", "SEASON", "TOURNAMENT"]))
    .optional(),
  scheduledStartTime: z.string(),
  sport: z.string(),
  sportCategory: z.enum(["CONVENTIONAL", "ESPORTS"]),
  tab: z.enum(["next", "live", "outrights"]),
  tournamentId: z.string().optional(),
  seasonId: z.string().optional(),
  visible: z.boolean(),
  streamUri: z.string().nullish(),
  expectedResults: z.record(z.string(), z.number()).optional(),
});

export type BettingEvent = zInfer<typeof bettingEvent>;
export type CampaignsType = PromotionCampaign;
